import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStats, getDAU, getSubscriberStats, getCohort } from "../lib/api";
import { SectionTitle, Loader } from "../components/Shared";
import { Sparkles, BookOpen, ListChecks, Map, Globe2, Layers, Users, Image as ImageIcon, ArrowRight, TrendingUp, Mail, Repeat } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";

const StatCard = ({ icon: Icon, label, value, accent = "terracotta", testid }) => (
  <div className="card p-6" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl grid place-items-center mb-4 ${
      accent === "terracotta" ? "bg-[#FBE7DF] text-[#E07A5F]" : "bg-[#EAECE6] text-[#3A5A40]"
    }`}>
      <Icon size={18} />
    </div>
    <div className="font-heading font-extrabold text-3xl md:text-4xl text-[#1A1A1A]">{value}</div>
    <div className="text-sm text-[#4A4A4A] mt-1">{label}</div>
  </div>
);

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function Impact() {
  const [s, setS] = useState(null);
  const [dau, setDau] = useState(null);
  const [subs, setSubs] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), getDAU(), getSubscriberStats(), getCohort()])
      .then(([stats, dauData, subStats, c]) => {
        setS(stats); setDau(dauData); setSubs(subStats); setCohort(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const chartData = (dau?.series || []).map((p) => ({ ...p, label: fmtDay(p.date) }));

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="impact-page">
      <SectionTitle
        kicker="Impact · live numbers"
        title="A new kind of learning, in motion."
        subtitle="Real-time platform metrics from the EduSphere AI Companion."
      />

      {loading && <Loader label="Loading impact…" />}
      {s && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="impact-stats-grid">
            <StatCard testid="stat-sessions" icon={Users} value={s.chat_sessions} label="Chat sessions started" accent="terracotta" />
            <StatCard testid="stat-stories" icon={BookOpen} value={s.stories_generated} label="Stories woven" accent="sage" />
            <StatCard testid="stat-illustrated" icon={ImageIcon} value={s.stories_illustrated} label="AI-illustrated stories" accent="terracotta" />
            <StatCard testid="stat-quizzes" icon={ListChecks} value={s.quizzes_generated} label="Quizzes generated" accent="sage" />
            <StatCard testid="stat-paths" icon={Map} value={s.learning_paths_generated} label="Learning paths created" accent="terracotta" />
            <StatCard testid="stat-messages" icon={Sparkles} value={s.messages_exchanged} label="Messages exchanged" accent="sage" />
            <StatCard testid="stat-subscribers" icon={Mail} value={subs?.total ?? 0} label="Daily digest subscribers" accent="terracotta" />
            <StatCard testid="stat-models" icon={Layers} value={s.stem_models_covered} label="STEM models covered" accent="sage" />
          </div>

          {/* DAU chart */}
          {dau && (
            <div className="mt-12 card p-6 md:p-8" data-testid="impact-dau-chart">
              <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold flex items-center gap-1.5">
                    <TrendingUp size={12} /> Engagement · last 14 days
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Daily learning sessions</h3>
                </div>
                <div className="flex gap-5">
                  <div data-testid="dau-total">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">Total · 14d</div>
                    <div className="font-heading font-extrabold text-2xl">{dau.total}</div>
                  </div>
                  <div data-testid="dau-peak">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">Peak day</div>
                    <div className="font-heading font-extrabold text-2xl">{dau.peak}</div>
                  </div>
                </div>
              </div>
              <div className="w-full h-[260px]">
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DCD7CE" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#4A4A4A", fontSize: 12 }} axisLine={{ stroke: "#DCD7CE" }} tickLine={false} />
                    <YAxis tick={{ fill: "#4A4A4A", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: "rgba(224,122,95,0.08)" }}
                      contentStyle={{ background: "#fff", border: "1px solid #DCD7CE", borderRadius: 12, fontSize: 13 }}
                      labelStyle={{ color: "#3A5A40", fontWeight: 600 }}
                    />
                    <Bar dataKey="sessions" fill="#E07A5F" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Cohort retention */}
          {cohort && cohort.cohorts.length > 0 && (
            <div className="mt-12 card p-6 md:p-8" data-testid="impact-cohort-chart">
              <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold flex items-center gap-1.5">
                    <Repeat size={12} /> Retention · last {cohort.cohorts.length} cohort weeks
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Cohort return rates</h3>
                  <p className="text-sm text-[#4A4A4A] mt-1 max-w-xl">% of learners from each signup week who came back within Day&nbsp;1, 3, or 7. Empty bars = window not yet elapsed.</p>
                </div>
              </div>
              <div className="w-full h-[260px]" style={{ minHeight: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={cohort.cohorts.map(c => ({
                    week: c.week,
                    "Day 1": c.day1 == null ? null : Math.round(c.day1 * 100),
                    "Day 3": c.day3 == null ? null : Math.round(c.day3 * 100),
                    "Day 7": c.day7 == null ? null : Math.round(c.day7 * 100),
                  }))} margin={{ top: 10, right: 16, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DCD7CE" vertical={false} />
                    <XAxis dataKey="week" tick={{ fill: "#4A4A4A", fontSize: 12 }} axisLine={{ stroke: "#DCD7CE" }} tickLine={false} />
                    <YAxis tick={{ fill: "#4A4A4A", fontSize: 12 }} axisLine={false} tickLine={false} unit="%" domain={[0, 100]} />
                    <Tooltip
                      cursor={{ stroke: "#DCD7CE" }}
                      contentStyle={{ background: "#fff", border: "1px solid #DCD7CE", borderRadius: 12, fontSize: 13 }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Line type="monotone" dataKey="Day 1" stroke="#E07A5F" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="Day 3" stroke="#3A5A40" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="Day 7" stroke="#F4A261" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Pillars / VC narrative */}
          <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="impact-pillars">
            {[
              { kicker: "Why now", title: "India's bilingual learning gap", body: "350M+ school learners; less than 8% of EdTech content blends mythology, vernacular, and hands-on STEM. EduSphere fills that gap with AI." },
              { kicker: "Moat", title: "World heritage + STEM + AI", body: "Story-rich learning across world mythology and folklore, the proven 50 EduSphere physical models, and AI tutoring in 12 languages." },
              { kicker: "Flywheel", title: "Every story is shareable", body: "AI-illustrated stories and quizzes turn each learner into a distributor. Streaks, badges and a daily story bring them back tomorrow." },
            ].map((p, i) => (
              <div key={i} className="card p-6" data-testid={`impact-pillar-${i}`}>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">{p.kicker}</div>
                <div className="font-heading text-xl font-bold text-[#1A1A1A] mt-2">{p.title}</div>
                <div className="text-sm text-[#4A4A4A] mt-2">{p.body}</div>
              </div>
            ))}
          </div>

          {/* Three-pillar EduSphere */}
          <div className="mt-12 card p-7 md:p-10" data-testid="impact-three-pillar">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Three pillar strategy</div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-1">Content · Gaming · Physical exploration</h2>
            <p className="text-[#4A4A4A] mt-3 max-w-3xl">
              EduSphere AI Companion is the digital backbone — feeding stories and explainers (Content), quizzes, streaks &amp; badges (Gaming),
              and pre-class primers for hands-on labs and pop-ups (Physical Exploration). One brand, three reinforcing channels.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/careers" className="btn-primary inline-flex items-center gap-2" data-testid="impact-cta-careers">
                Explore career pathways <ArrowRight size={14} />
              </Link>
              <Link to="/library" className="btn-outline inline-flex items-center gap-2" data-testid="impact-cta-library">
                Browse the 50 STEM models
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
